/**
 * Email Service - TROIA V3
 * Reutiliza chatService com filtros específicos para channelType='email'
 */

import { api, apiDelete, apiGet, apiPost } from '@/lib/api';
import * as chatService from '@/modules/chat/services/chatService';
import type {
  ConversationQuery,
  ConversationListResponse,
  ConversationResponse,
} from '@troia-v3/types';
import type { ComposeEmailData, EmailFolderType } from '../types/email.types';
import { FOLDER_TO_FILTER } from '../constants/folders';

/**
 * Buscar conversas de email com filtros de pasta
 */
export const getEmailConversations = async (
  folder: EmailFolderType,
  page: number = 1,
  limit: number = 25,
  additionalFilters?: Record<string, unknown>,
  signal?: AbortSignal
): Promise<ConversationListResponse> => {
  const folderFilters = FOLDER_TO_FILTER[folder] || {};

  const query: ConversationQuery = {
    page,
    limit,
    filters: {
      source: 'email',
      ...folderFilters,
      ...additionalFilters,
    } as ConversationQuery['filters'],
  };

  return chatService.getConversations(query, signal);
};

/**
 * Buscar detalhes de uma conversa de email
 */
export const getEmailConversation = async (
  id: string,
  signal?: AbortSignal
): Promise<ConversationResponse> => {
  return chatService.getConversationById(id, signal);
};

/**
 * Buscar mensagens de uma thread de email
 */
export const getEmailMessages = async (
  conversationId: string,
  page: number = 1,
  limit: number = 50,
  signal?: AbortSignal
) => {
  return chatService.getMessages(
    { conversationId, page, limit },
    signal
  );
};

/**
 * Enviar email (nova conversa ou reply)
 */
export const sendEmail = async (
  data: ComposeEmailData,
  signal?: AbortSignal
) => {
  if (data.replyToConversationId) {
    return chatService.sendMessage({
      conversationId: data.replyToConversationId,
      content: [{ type: 'text', text: data.body }],
    }, signal);
  }

  // Novo email: criar conversa + enviar mensagem
  const conversation = await chatService.createConversation({
    channelId: data.channelId || '',
    source: 'email',
    subject: data.subject,
    metadata: {
      to: data.to,
      cc: data.cc,
      bcc: data.bcc,
    },
  } as unknown as Parameters<typeof chatService.createConversation>[0], signal);

  if (conversation?.id) {
    return chatService.sendMessage({
      conversationId: conversation.id,
      content: [{ type: 'text', text: data.body }],
    }, signal);
  }

  return conversation;
};

/**
 * Marcar email como lido
 */
export const markAsRead = async (
  conversationId: string,
  messageIds: string[],
  signal?: AbortSignal
) => {
  return chatService.markConversationAsRead(conversationId, messageIds, signal);
};

/**
 * Atualizar conversa (star, flag, mover pasta)
 */
export const updateEmailConversation = async (
  id: string,
  data: Record<string, unknown>,
  signal?: AbortSignal
) => {
  return chatService.updateConversation(
    id,
    data as Parameters<typeof chatService.updateConversation>[1],
    signal
  );
};

/**
 * Buscar canais de email configurados
 */
export const getEmailChannels = async (
  signal?: AbortSignal
) => {
  return apiGet<Array<{ id: string; name: string; type: string; config?: Record<string, unknown> }>>(
    '/channels?type=email',
    { signal }
  );
};

/**
 * Buscar contagem de não lidos para email
 */
export const getEmailUnreadCount = async (
  signal?: AbortSignal
): Promise<number> => {
  try {
    const response = await apiGet<{ count: number }>('/conversations/unread-count/email', { signal });
    return response.count;
  } catch {
    return 0;
  }
};

/**
 * Operação bulk em conversas de email
 */
export const emailBulkOperation = async (
  conversationIds: string[],
  operation: 'addTag' | 'removeTag' | 'close' | 'changeStatus' | 'markUnread',
  data?: Record<string, unknown>,
): Promise<void> => {
  await apiPost('/conversations/bulk', { conversationIds, operation, data });
};

/** Arquivar: tag 'archived' + status 'closed' */
export const archiveEmail = async (id: string): Promise<void> => {
  await emailBulkOperation([id], 'addTag', { tag: 'archived' });
  await emailBulkOperation([id], 'changeStatus', { status: 'closed' });
};

/** Favoritar: tag 'starred' */
export const starEmail = async (id: string): Promise<void> => {
  await emailBulkOperation([id], 'addTag', { tag: 'starred' });
};

/** Desfavoritar: remove tag 'starred' */
export const unstarEmail = async (id: string): Promise<void> => {
  await emailBulkOperation([id], 'removeTag', { tag: 'starred' });
};

/** Lixeira: tag 'trash' + status 'closed' */
export const trashEmail = async (id: string): Promise<void> => {
  await emailBulkOperation([id], 'addTag', { tag: 'trash' });
  await emailBulkOperation([id], 'changeStatus', { status: 'closed' });
};

/**
 * Spam: marca a conversa E registra o remetente na lista de bloqueados, para
 * que os próximos e-mails dele já cheguem na pasta Spam.
 *
 * O endereço é resolvido no backend a partir do `contactId` — o front não tem
 * o e-mail com segurança (o nome do contato é o display name do remetente
 * quando ele manda um, não o endereço).
 *
 * O bloqueio é BEST-EFFORT: se falhar, a conversa continua marcada como spam
 * (o efeito que o usuário pediu). Derrubar a ação inteira por causa do
 * cadastro do remetente seria pior.
 */
export const spamEmail = async (id: string, contactId?: string): Promise<void> => {
  await emailBulkOperation([id], 'addTag', { tag: 'spam' });
  if (!contactId) return;
  try {
    await apiPost('/email-domains/spam-senders', { contactId, conversationId: id });
  } catch (error) {
    console.warn('[email] Conversa marcada como spam, mas o remetente não foi bloqueado:', error);
  }
};

/**
 * Restaurar: remove a tag e reabre. Saindo da pasta Spam, o remetente também
 * sai da lista de bloqueados — senão o próximo e-mail dele voltaria pro spam e
 * o "Não é spam" pareceria não ter funcionado.
 */
export const restoreEmail = async (id: string, fromTag: string, contactId?: string): Promise<void> => {
  await emailBulkOperation([id], 'removeTag', { tag: fromTag });
  await emailBulkOperation([id], 'changeStatus', { status: 'active' });
  if (fromTag !== 'spam' || !contactId) return;
  try {
    await apiPost('/email-domains/spam-senders/unblock', { contactId });
  } catch (error) {
    console.warn('[email] Conversa restaurada, mas o remetente continua bloqueado:', error);
  }
};

/**
 * Marcar como não lido — operação POR USUÁRIO no backend.
 * Antes usava `changeStatus` com `{ unread: true }`, campo que o Zod descartava
 * silenciosamente e que fazia o backend responder 400 ("Status is required").
 */
export const markEmailAsUnread = async (id: string): Promise<void> => {
  await emailBulkOperation([id], 'markUnread');
};

/**
 * Excluir permanentemente (só faz sentido a partir da Lixeira).
 * `DELETE /conversations/:id` é soft delete: a conversa recebe `deletedAt`,
 * some de toda listagem e leva junto as mensagens (cascata no backend).
 */
export const deleteEmail = async (id: string): Promise<void> => {
  await apiDelete(`/conversations/${id}`);
};

/** Buscar contagem de emails por pasta */
export const getEmailFolderCount = async (
  folder: EmailFolderType,
  signal?: AbortSignal
): Promise<number> => {
  try {
    const result = await getEmailConversations(folder, 1, 1, undefined, signal);
    return result.total || 0;
  } catch {
    return 0;
  }
};

/** Upload de arquivo para email (retorna URL e metadata) */
export const uploadEmailAttachment = async (file: File): Promise<{ url: string; originalName: string; mimeType: string; size: number }> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('uploads?folder=email-attachments', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data;
};

const emailService = {
  getEmailConversations,
  getEmailConversation,
  getEmailMessages,
  sendEmail,
  markAsRead,
  updateEmailConversation,
  getEmailChannels,
  getEmailUnreadCount,
  getEmailFolderCount,
  emailBulkOperation,
  archiveEmail,
  starEmail,
  unstarEmail,
  trashEmail,
  spamEmail,
  restoreEmail,
  markEmailAsUnread,
  uploadEmailAttachment,
};

export default emailService;
